<template>
  <div
    ref="navbarNode"
    class="nav"
  >
    <template v-if="!navIsLoading">
      <div
        v-for="navEl in navRes"
        :key="navEl.dataset"
        :class="['nav-el', `nav-el-${navEl.dataset}`, { 'nav-el-active': dataSet === navEl.dataset }]"
        @click="goTo(navEl.dataset)"
      >
        {{ navEl.title }}
      </div>
    </template>
    <template v-else>
      <UiSkeletonItem
        v-for="el in 3"
        :key="el"
        class="skeleton"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
  import { UiSkeletonItem } from '@experience/vue3-kit';
  import { nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';

  import { trackEvent } from '@/shared/lib/analytics/compat';
  import type { SightModalNav } from '@/shared/types/sight-modal';

  interface NavItem {
    dataset: keyof SightModalNav;
    title: string;
  }

  const NAV_ITEMS: NavItem[] = [
    { dataset: 'info', title: 'Обзор' },
    { dataset: 'position', title: 'Расположение' },
    { dataset: 'guides', title: 'Гиды рассказывают' },
    { dataset: 'experience', title: 'Экскурсии' },
  ];

  // Соответствие внутренних идентификаторов вкладок названиям для аналитики
  const TAB_ANALYTICS_NAME: Record<string, string> = {
    info: 'overview',
    position: 'location',
    guides: 'guides',
    experience: 'experiences',
  };

  const props = withDefaults(
    defineProps<{
      modalNav: SightModalNav;
      navIsLoading?: boolean;
      analyticsBase?: Record<string, unknown>;
    }>(),
    {
      navIsLoading: false,
      analyticsBase: () => ({}),
    },
  );

  const navbar = useTemplateRef('navbarNode');

  const navRes = ref<NavItem[]>([]);
  const dataSet = ref<string>('info');

  let scrollContainer: HTMLElement | null = null;
  let observedNodes: HTMLElement[] = [];
  let isUserScrolling = false;
  let userScrollResetTimer: ReturnType<typeof setTimeout> | null = null;

  // Порог в пикселях от верха скролл-контейнера — секция считается активной,
  // если её верх пересекает эту линию.
  const ACTIVE_OFFSET = 120;

  const getRoot = (): Document | ShadowRoot | null => {
    const root = navbar.value?.getRootNode();

    return root instanceof Document || root instanceof ShadowRoot ? root : null;
  };

  const rebuildNavItems = () => {
    navRes.value = NAV_ITEMS.filter((item) => props.modalNav[item.dataset]);
  };

  const setActive = (attr: string) => {
    if (dataSet.value === attr) return;

    dataSet.value = attr;

    const root = getRoot();
    const nodeToScroll = root?.querySelector<HTMLElement>(`.nav-el-${attr}`);
    const navEl = navbar.value;

    if (navEl && nodeToScroll) {
      navEl.scrollLeft = nodeToScroll.offsetLeft;
    }
  };

  const updateActiveByScroll = () => {
    if (!scrollContainer || !observedNodes.length) return;

    const containerTop = scrollContainer.getBoundingClientRect().top;

    let activeAttr: string | null = null;

    for (const node of observedNodes) {
      const { attr } = node.dataset;
      const nodeTop = node.getBoundingClientRect().top - containerTop;

      if (nodeTop > ACTIVE_OFFSET) {
        break;
      }

      if (attr) {
        activeAttr = attr;
      }
    }

    if (activeAttr) {
      setActive(activeAttr);
    }
  };

  const onScroll = () => {
    if (isUserScrolling) {
      if (userScrollResetTimer) clearTimeout(userScrollResetTimer);

      userScrollResetTimer = setTimeout(() => {
        isUserScrolling = false;
      }, 400);

      return;
    }

    updateActiveByScroll();
  };

  const stopObserving = () => {
    scrollContainer?.removeEventListener('scroll', onScroll);
    scrollContainer = null;
    observedNodes = [];
  };

  const startObserving = () => {
    void nextTick(() => {
      stopObserving();

      const root = getRoot();

      if (!root) return;

      scrollContainer = root.querySelector<HTMLElement>('.ui-modal__scroll-container');

      const nodes = Array.from(root.querySelectorAll<HTMLElement>('.obs-el')).filter((node) => node.dataset.attr);

      if (!nodes.length || !scrollContainer) return;

      // Сортируем по фактическому положению в потоке (offsetTop) — порядок секций в модалке.
      observedNodes = nodes.sort((a, b) => a.offsetTop - b.offsetTop);

      scrollContainer.addEventListener('scroll', onScroll, { passive: true });
      updateActiveByScroll();
    });
  };

  const goTo = (id: string) => {
    const root = getRoot();
    const targetElement = root?.querySelector<HTMLElement>(`#${id}`);

    if (!targetElement) return;

    void trackEvent('around:sights:sight-popup:tab.click', {
      ...props.analyticsBase,
      tab_name: TAB_ANALYTICS_NAME[id] ?? id,
    });

    isUserScrolling = true;
    dataSet.value = id;
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  onMounted(() => {
    setTimeout(startObserving, 500);
  });

  onBeforeUnmount(() => {
    if (userScrollResetTimer) clearTimeout(userScrollResetTimer);
    stopObserving();
  });

  watch(
    () => props.modalNav,
    () => {
      rebuildNavItems();
      startObserving();
    },
    { deep: true, immediate: true },
  );
</script>

<style lang="scss" scoped>
  .nav {
    display: flex;
    padding: 0 var(--alias-space-8);
    overflow-x: scroll;
    scrollbar-width: none;
    background: var(--alias-color-background-default);
    border-bottom: 1px solid var(--alias-color-border-default);
    -ms-overflow-style: none;
  }

  .nav-el {
    padding: var(--alias-space-12) var(--alias-space-8);
    color: var(--alias-color-text-subtle);
    white-space: nowrap;
    cursor: pointer;

    @include base-400-300-regular;
  }

  .nav-el-active {
    color: var(--alias-color-text-default);
    border-bottom: 2px solid var(--alias-color-border-focused);
  }

  .skeleton {
    width: 100px;
    height: 22px;
    margin: var(--alias-space-12) var(--alias-space-8);
    border-radius: var(--alias-radius-8);
  }
</style>
